import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/api-client';

// Booking inquiry types based on design document
export interface BookingInquiry {
  inquiryId: string;
  listingId: string;
  listingTitle: string; // Denormalized listing title for better UX
  hostId: string;
  guestInfo: {
    name: string;
    email: string;
    whatsappNumber: string;
    instagramId?: string;
  };
  stayDetails: {
    checkInDate: string;
    checkOutDate: string;
    guestCount: number;
  };
  additionalNotes?: string;
  status: 'pending' | 'responded' | 'confirmed' | 'declined';
  createdAt: string;
  updatedAt: string;
}

export interface CreateInquiryRequest {
  listingId: string;
  listingTitle: string; // Include listing title for denormalization
  guestInfo: {
    name: string;
    email: string;
    whatsappNumber: string;
    instagramId?: string;
  };
  stayDetails: {
    checkInDate: string;
    checkOutDate: string;
    guestCount: number;
  };
  additionalNotes?: string;
}

export interface UpdateInquiryStatusRequest {
  status: 'responded' | 'confirmed' | 'declined';
}

// Query keys
export const bookingInquiryKeys = {
  all: ['bookingInquiries'] as const,
  lists: () => [...bookingInquiryKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...bookingInquiryKeys.lists(), { filters }] as const,
  details: () => [...bookingInquiryKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingInquiryKeys.details(), id] as const,
  hostInquiries: (hostId: string) => [...bookingInquiryKeys.all, 'host', hostId] as const,
};

// Create booking inquiry (for guests)
export const useCreateBookingInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInquiryRequest): Promise<BookingInquiry> => {
      const response = await apiClient.post<BookingInquiry>('/inquiries', data);
      return response.data;
    },
    onSuccess: (newInquiry) => {
      // Invalidate host inquiries list if we have it cached
      queryClient.invalidateQueries({ 
        queryKey: bookingInquiryKeys.hostInquiries(newInquiry.hostId) 
      });
      
      // Set individual inquiry cache
      queryClient.setQueryData(
        bookingInquiryKeys.detail(newInquiry.inquiryId), 
        newInquiry
      );
    },
  });
};

// Get inquiries for authenticated host
export const useHostInquiries = (filters?: { 
  status?: string; 
  listingId?: string; 
  limit?: number; 
  offset?: number; 
}) => {
  return useQuery({
    queryKey: bookingInquiryKeys.list(filters || {}),
    queryFn: async (): Promise<BookingInquiry[]> => {
      const params = new URLSearchParams();
      
      if (filters?.status) params.append('status', filters.status);
      if (filters?.listingId) params.append('listingId', filters.listingId);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());
      
      const endpoint = `/inquiries${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<{ inquiries: BookingInquiry[]; totalCount: number }>(endpoint);
      return response.data?.inquiries || [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute - inquiries should be fresh
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get single inquiry detail
export const useBookingInquiry = (inquiryId: string) => {
  const isValidId = Boolean(inquiryId && inquiryId.trim() !== '' && inquiryId !== 'undefined' && inquiryId !== 'null');
  
  return useQuery({
    queryKey: bookingInquiryKeys.detail(inquiryId),
    queryFn: async (): Promise<BookingInquiry> => {
      if (!isValidId) {
        throw new Error('Invalid inquiry ID provided');
      }
      const response = await apiClient.get<BookingInquiry>(`/inquiries/${inquiryId}`);
      return response.data;
    },
    enabled: isValidId,
    staleTime: 1 * 60 * 1000, // 1 minute
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Update inquiry status (for hosts)
export const useUpdateInquiryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      inquiryId, 
      status 
    }: { 
      inquiryId: string; 
      status: 'responded' | 'confirmed' | 'declined' 
    }): Promise<BookingInquiry> => {
      const response = await apiClient.put<BookingInquiry>(
        `/inquiries/${inquiryId}`, 
        { status }
      );
      return response.data;
    },
    onMutate: async ({ inquiryId, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: bookingInquiryKeys.detail(inquiryId) });

      // Snapshot the previous value
      const previousInquiry = queryClient.getQueryData<BookingInquiry>(
        bookingInquiryKeys.detail(inquiryId)
      );

      // Optimistically update to the new value
      if (previousInquiry) {
        const updatedInquiry = {
          ...previousInquiry,
          status,
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData<BookingInquiry>(
          bookingInquiryKeys.detail(inquiryId), 
          updatedInquiry
        );
        
        // Update in host inquiries list as well
        queryClient.setQueryData<BookingInquiry[]>(
          bookingInquiryKeys.list({}),
          (old) => old ? old.map(inquiry => 
            inquiry.inquiryId === inquiryId ? updatedInquiry : inquiry
          ) : []
        );
      }

      return { previousInquiry };
    },
    onError: (err, { inquiryId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousInquiry) {
        queryClient.setQueryData(
          bookingInquiryKeys.detail(inquiryId), 
          context.previousInquiry
        );
      }
    },
    onSettled: (data, error, { inquiryId }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: bookingInquiryKeys.detail(inquiryId) });
      queryClient.invalidateQueries({ queryKey: bookingInquiryKeys.lists() });
    },
  });
};

// Get inquiry statistics for host dashboard
export const useInquiryStats = () => {
  return useQuery({
    queryKey: [...bookingInquiryKeys.all, 'stats'],
    queryFn: async () => {
      const response = await apiClient.get<{
        total: number;
        pending: number;
        responded: number;
        confirmed: number;
        declined: number;
        thisMonth: number;
        responseRate: number;
      }>('/inquiries/stats');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Custom hook for inquiry management
export const useInquiryManagement = () => {
  const queryClient = useQueryClient();

  const markAsRead = (inquiryId: string) => {
    // Optimistically mark as read in cache
    queryClient.setQueryData<BookingInquiry>(
      bookingInquiryKeys.detail(inquiryId),
      (old) => old ? { ...old, isRead: true } : old
    );
  };

  const refreshInquiries = () => {
    queryClient.invalidateQueries({ queryKey: bookingInquiryKeys.lists() });
  };

  const prefetchInquiry = (inquiryId: string) => {
    queryClient.prefetchQuery({
      queryKey: bookingInquiryKeys.detail(inquiryId),
      queryFn: async (): Promise<BookingInquiry> => {
        const response = await apiClient.get<BookingInquiry>(`/inquiries/${inquiryId}`);
        return response.data;
      },
      staleTime: 1 * 60 * 1000,
    });
  };

  return {
    markAsRead,
    refreshInquiries,
    prefetchInquiry,
  };
};